/// <amd-module name="Artifacts/Connect/tools/Cargo" />
import * as Res from "Artifacts/Connect/Resources";
import { CopyableCode, DetailsCard, PublishDetailsCard } from "Artifacts/Connect/components/Common";
import { PackagingService, TfsService } from "Artifacts/Connect/services/PackagingService";
import { ToolService } from "Artifacts/Connect/services/ToolService";
import { IToolConnectProps } from "Artifacts/Connect/tools/IToolConnectProps";
import { ICombinedFeedView } from "Artifacts/Connect/types/ICombinedFeedView";
import { IProtocol } from "Artifacts/Connect/types/IProtocol";
import { ITool, ToolId } from "Artifacts/Connect/types/ITool";
import { FWLink } from "Artifacts/Root/components/FWLink";
import { getIsHosted } from "Artifacts/Root/helpers/CollectionHelpers";
import { UpstreamSourceType } from "Artifacts/Root/web-apis/contracts/Feed";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IPageData } from "VSS/Platform/Page";
import { replaceRouteValues } from "VSS/Platform/Util/Url";
import { Link } from "VSSUI/Link";
import { Tab, TabBar, TabSize } from "VSSUI/Tabs";
import * as React from "react";
import { useMemo } from "react";
import { OperatingSystem } from "../Constants";

const platformCredProviders: { [key: string]: string } = {
  [OperatingSystem.Windows]: "cargo:wincred",
  [OperatingSystem.Linux]: "cargo:libsecret",
  [OperatingSystem.MacOs]: "cargo:macos-keychain",
};

const platformUserConfigPaths: { [key: string]: string } = {
  [OperatingSystem.Windows]: "%USERPROFILE%\\.cargo\\config.toml",
  [OperatingSystem.Linux]: "~/.cargo/config.toml",
  [OperatingSystem.MacOs]: "~/.cargo/config.toml",
};

const CargoConnectComponent: React.FC<IToolConnectProps<CargoProtocol>> = ({
  pageContext,
  protocol,
  feed,
  packagingService,
  tfsService,
}) => {
  const tfsServiceUrl = tfsService.getSessionTokenUri();
  const feedUrl = protocol.getFeedUrl(packagingService, feed);
  const hasPublicUpstreamSource = feed.upstreamSources.some(
    (x) => x.upstreamSourceType === UpstreamSourceType.Public,
  );
  const isHosted = useMemo(() => getIsHosted(pageContext), [pageContext]);
  const [selectedTabId, setSelectedTabId] = React.useState<string>(OperatingSystem.Windows);

  return (
    <React.Fragment>
      <DetailsCard title={{ text: Res.ProjectSetup, ariaLevel: 3 }}>
        <FormatComponent format={Res.Cargo_Config}>
          <FWLink linkId="2234410">
            <code>.cargo/config.toml</code>
          </FWLink>
        </FormatComponent>
        <CopyableCode ariaLabel={Res.ProjectSetup}>
          <pre>{`[registries]
${feed.name} = { index = "${feedUrl}" }`}</pre>
        </CopyableCode>

        {hasPublicUpstreamSource && (
          <FormatComponent format={Res.Cargo_ConfigWithPublicUpstream}>
            <code>.cargo/config.toml</code>
            <a href="https://crates.io/">crates.io</a>
            {feed.name}
          </FormatComponent>
        )}
        {hasPublicUpstreamSource && (
          <CopyableCode ariaLabel={Res.ProjectSetup}>
            <pre>{`[source.crates-io]
replace-with = "${feed.name}"`}</pre>
          </CopyableCode>
        )}
      </DetailsCard>

      <DetailsCard title={{ text: Res.Cargo_ConfigureACredentialProviderHeader, ariaLevel: 3 }}>
        <p>
          <FormatComponent format={Res.Cargo_CredentialProviderRequired}>
            <FWLink linkId="2248942">{Res.Cargo_CargoBook}</FWLink>
          </FormatComponent>
        </p>
        <TabBar
          selectedTabId={selectedTabId}
          onSelectedTabChanged={setSelectedTabId}
          tabSize={TabSize.Compact}
        >
          <Tab name={Res.Cargo_WindowsTab} id={OperatingSystem.Windows} />
          <Tab name={Res.Cargo_LinuxTab} id={OperatingSystem.Linux} />
          <Tab name={Res.Cargo_MacOsTab} id={OperatingSystem.MacOs} />
        </TabBar>
        <p>
          <FormatComponent format={Res.Cargo_CredentialProviderSuggestion}>
            <code>{platformUserConfigPaths[selectedTabId]}</code>
          </FormatComponent>
        </p>
        <CopyableCode ariaLabel={Res.Cargo_CredentialProviderSettingsLabel}>
          <pre>
            [registry]{"\n"}
            global-credential-providers = ["cargo:token", "{platformCredProviders[selectedTabId]}"]
          </pre>
        </CopyableCode>
      </DetailsCard>

      <DetailsCard title={{ text: Res.Cargo_LoginHeader, ariaLevel: 3 }}>
        <p>
          <FormatComponent format={Res.GeneratePAT}>
            <Link underline href={tfsServiceUrl} target="_blank">
              {Res.PersonalAccessToken}
            </Link>
            <em>{Res.Packaging}</em>
          </FormatComponent>
        </p>
        <p>{Res.Cargo_Login}</p>
        <p>{Res.Cargo_AuthWithPowerShellAndPAT}</p>
        <p>{Res.Cargo_PowerShellCommandUsageInfo}</p>
        {/* prettier-ignore */}
        <CopyableCode ariaLabel={Res.ProjectSetup}>
          "Basic " + [Convert]::ToBase64String([Text.Encoding]::UTF8.GetBytes("PAT:" + (Read-Host -MaskInput "Enter PAT")))
        </CopyableCode>
        <CopyableCode>
          cargo login --registry {feed.name}
        </CopyableCode>
        <p>{Res.Cargo_AuthWithBashAndPAT}</p>
        <CopyableCode ariaLabel={Res.ProjectSetup}>
          read -p "Enter PAT: " PAT; echo Basic $(echo -n PAT:$PAT | base64) | cargo login
          --registry {feed.name}
        </CopyableCode>

        {isHosted && (
          <React.Fragment>
            <p>{Res.Cargo_AuthWithAzureCLI}</p>
            <CopyableCode ariaLabel={Res.ProjectSetup}>
              {/* prettier-ignore  */}
              <pre>
                az login{"\n"}
                az account get-access-token --query "join(' ', ['Bearer', accessToken])" --output tsv | cargo login --registry {feed.name}
              </pre>
            </CopyableCode>
          </React.Fragment>
        )}
      </DetailsCard>

      <PublishDetailsCard feed={feed}>
        <p>{Res.RunCommand_Directory}</p>
        <CopyableCode ariaLabel={Res.PublishDetails_Cargo}>cargo publish</CopyableCode>
      </PublishDetailsCard>
    </React.Fragment>
  );
};

class CargoProtocol implements IProtocol {
  readonly name = "Cargo";
  public readonly area = "Cargo";
  getFeedUrl(packagingService: PackagingService, feed: ICombinedFeedView) {
    const path = replaceRouteValues("_packaging/{feedId}/{area}/{resource}", {
      feedId: feed.name,
      area: this.area,
      resource: "index",
    });

    return "sparse+" + packagingService.getUrl(feed.project, path + "/");
  }
}

export class CargoTool implements ITool {
  readonly id = ToolId.Cargo;
  readonly name = Res.Cargo;
  readonly protocol = new CargoProtocol();
  readonly allowNonMembers = false;
  readonly imagePath = `ms.azure-artifacts/connect/img/rust.svg`;
  readonly Component: React.ComponentType = CargoConnectComponent;
  readonly toolLinkId = 2208434;

  public getToolLink = () => {
    return {
      href: `https://go.microsoft.com/fwlink/?linkid=${this.toolLinkId}`,
      children: `${Res.CommandLineReference}`,
    };
  };

  public isEnvironmentSupported(pageData: IPageData): boolean {
    return true;
  }

  public isProtocolEnabled(pageContext: IVssPageContext): boolean {
    return (
      isFeatureFlagEnabled(pageContext, "Packaging.Feed.Cargo.EnableUI", false) ||
      isFeatureFlagEnabled(pageContext, "Packaging.Feed.Cargo.EnableUI.MSFT", false)
    );
  }

  supportsGet(): boolean {
    return true;
  }

  supportsPush(feed: ICombinedFeedView): boolean {
    return feed.canPublishPackages;
  }

  renderComponent(
    pageContext: IVssPageContext,
    feed: ICombinedFeedView,
    packagingService: PackagingService,
    tfsService: TfsService,
  ): JSX.Element {
    return (
      <CargoConnectComponent
        pageContext={pageContext}
        protocol={this.protocol}
        feed={feed}
        packagingService={packagingService}
        tfsService={tfsService}
      />
    );
  }
}

ToolService.registerTool(new CargoTool());
